import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import paymentService, { PaymentTransaction } from '../services/paymentService';
import { trackEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

interface PaymentStatusPageProps {
  status: 'success' | 'pending' | 'error';
}

const PaymentStatusPage: React.FC<PaymentStatusPageProps> = ({ status }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentTransaction, setPaymentTransaction] = useState<PaymentTransaction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orderId = searchParams.get('order_id');
  const transactionId = searchParams.get('transaction_id');

  useEffect(() => {
    if (orderId || transactionId) {
      checkPaymentStatus();
    } else {
      setIsLoading(false);
      setError(t('errors.paymentParamsInvalid'));
    }
    // Fire the analytics event based on which callback page mounted.
    // success/pending/error are the three possible landing routes;
    // maps to payment_success / payment_pending / payment_failed.
    // Fires once per mount -- deps intentionally include the status
    // prop so navigating between statuses (rare) also tracks.
    if (status === 'success') {
      trackEvent('payment_success', { order_id: orderId || undefined });
    } else if (status === 'error') {
      trackEvent('payment_failed', { order_id: orderId || undefined });
    }
    // Note: pending is a transient state (waiting for bank transfer /
    // e-wallet confirmation). We don't count it as a conversion yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, transactionId, status]);

  const checkPaymentStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (orderId) {
        // Verify payment by order_id - this calls Midtrans API to sync status
        // Works for both Snap.js popup callback and Midtrans page redirect
        const response = await paymentService.verifyPaymentByOrderId(orderId);
        if (response.success && response.payment_transaction) {
          setPaymentTransaction(response.payment_transaction);
        } else {
          throw new Error('Transaksi pembayaran tidak ditemukan / Payment transaction not found');
        }
      } else if (transactionId) {
        // Fallback: check by transaction ID
        const response = await paymentService.getPaymentStatus(transactionId);
        if (response.success && response.payment_transaction) {
          setPaymentTransaction(response.payment_transaction);
        } else {
          throw new Error('Transaksi pembayaran tidak ditemukan / Payment transaction not found');
        }
      }
    } catch (error: any) {
      console.error('Failed to check payment status:', error);
      setError(error.message || t('payment.toast.checkStatusFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = () => {
    if (paymentTransaction) {
      const paymentStatus = paymentTransaction.status;
      
      switch (paymentStatus) {
        case 'SUCCESS':
          return {
            icon: <CheckCircleIcon className="w-16 h-16 text-green-500" />,
            title: 'Pembayaran Berhasil',
            titleEn: 'Payment Successful',
            color: 'success' as const,
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200'
          };
        case 'PENDING':
          return {
            icon: <ScheduleIcon className="w-16 h-16 text-yellow-500" />,
            title: 'Pembayaran Sedang Diproses',
            titleEn: 'Payment Processing',
            color: 'warning' as const,
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200'
          };
        case 'FAILED':
        case 'EXPIRED':
        case 'CANCELLED':
        default:
          return {
            icon: <ErrorIcon className="w-16 h-16 text-red-500" />,
            title: 'Pembayaran Gagal',
            titleEn: 'Payment Failed',
            color: 'error' as const,
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200'
          };
      }
    }

    // Fallback based on URL status
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircleIcon className="w-16 h-16 text-green-500" />,
          title: 'Pembayaran Berhasil',
          titleEn: 'Payment Successful',
          color: 'success' as const,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'pending':
        return {
          icon: <ScheduleIcon className="w-16 h-16 text-yellow-500" />,
          title: 'Pembayaran Sedang Diproses',
          titleEn: 'Payment Processing',
          color: 'warning' as const,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      case 'error':
      default:
        return {
          icon: <ErrorIcon className="w-16 h-16 text-red-500" />,
          title: 'Pembayaran Gagal',
          titleEn: 'Payment Failed',
          color: 'error' as const,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
    }
  };

  const getStatusMessage = () => {
    if (paymentTransaction) {
      const paymentStatus = paymentTransaction.status;
      
      switch (paymentStatus) {
        case 'SUCCESS':
          return {
            message: 'Pembayaran Anda telah berhasil diproses. Terima kasih!',
            messageEn: 'Your payment has been successfully processed. Thank you!',
            details: 'Invoice akan segera diperbarui statusnya dan Anda akan menerima konfirmasi via email.',
            detailsEn: 'The invoice status will be updated shortly and you will receive an email confirmation.'
          };
        case 'PENDING':
          return {
            message: 'Pembayaran Anda sedang diproses oleh penyedia layanan pembayaran.',
            messageEn: 'Your payment is being processed by the payment provider.',
            details: 'Silakan tunggu atau periksa kembali status pembayaran dalam beberapa menit.',
            detailsEn: 'Please wait or check the payment status again in a few minutes.'
          };
        case 'FAILED':
          return {
            message: 'Pembayaran gagal diproses. Silakan coba lagi.',
            messageEn: 'Payment failed to process. Please try again.',
            details: 'Jika masalah berlanjut, silakan hubungi layanan pelanggan kami.',
            detailsEn: 'If the problem persists, please contact our customer service.'
          };
        case 'EXPIRED':
          return {
            message: 'Sesi pembayaran telah berakhir.',
            messageEn: 'Payment session has expired.',
            details: 'Silakan buat transaksi pembayaran baru untuk melanjutkan.',
            detailsEn: 'Please create a new payment transaction to continue.'
          };
        case 'CANCELLED':
          return {
            message: 'Pembayaran dibatalkan.',
            messageEn: 'Payment was cancelled.',
            details: 'Anda dapat mencoba melakukan pembayaran lagi kapan saja.',
            detailsEn: 'You can try to make the payment again at any time.'
          };
        default:
          return {
            message: 'Status pembayaran tidak diketahui.',
            messageEn: 'Unknown payment status.',
            details: 'Silakan hubungi layanan pelanggan untuk bantuan.',
            detailsEn: 'Please contact customer service for assistance.'
          };
      }
    }

    return {
      message: 'Status pembayaran sedang diproses...',
      messageEn: 'Payment status is being processed...',
      details: 'Mohon tunggu sebentar.',
      detailsEn: 'Please wait a moment.'
    };
  };

  const statusConfig = getStatusConfig();
  const statusMessage = getStatusMessage();

  if (isLoading) {
    return (
      <Container maxWidth="md" className="py-8">
        <Paper className="p-8 text-center">
          <CircularProgress size={48} />
          <Typography variant="h6" className="mt-4">
            {t('payment.checkingStatus')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" className="py-8">
        <Paper className="p-8">
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
          
          <div className="text-center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/invoices')}
              startIcon={<HomeIcon />}
              className="mr-4"
            >
              Kembali ke Invoice / Back to Invoices
            </Button>
            
            <Button
              variant="outlined"
              onClick={checkPaymentStatus}
              startIcon={<RefreshIcon />}
            >
              Coba Lagi / Try Again
            </Button>
          </div>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="py-8">
      <Paper className={`${statusConfig.bgColor} border ${statusConfig.borderColor} overflow-hidden`}>
        {/* Header */}
        <Box className="text-center py-8 px-4">
          <div className="flex justify-center mb-4">
            {statusConfig.icon}
          </div>
          
          <Typography variant="h4" className="font-bold mb-2">
            {statusConfig.title}
          </Typography>
          
          <Typography variant="h6" color="textSecondary" className="mb-4">
            {statusConfig.titleEn}
          </Typography>

          <Typography variant="body1" className="mb-2">
            {statusMessage.message}
          </Typography>
          
          <Typography variant="body2" color="textSecondary">
            {statusMessage.messageEn}
          </Typography>
        </Box>

        <Divider />

        {/* Payment Details */}
        {paymentTransaction && (
          <Box className="p-6">
            <Typography variant="h6" className="mb-4">
              Detail Pembayaran / Payment Details
            </Typography>
            
            <Card variant="outlined" className="mb-4">
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      ID Transaksi / Transaction ID
                    </Typography>
                    <Typography variant="body1" className="font-mono">
                      {paymentTransaction.midtrans_transaction_id || paymentTransaction.midtrans_order_id}
                    </Typography>
                  </div>
                  
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      Jumlah / Amount
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {paymentTransaction.formatted_amount}
                    </Typography>
                  </div>
                  
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      {t('orders.status')}
                    </Typography>
                    <Chip 
                      label={`${paymentService.getPaymentStatusText(paymentTransaction.status)} / ${paymentService.getPaymentStatusText(paymentTransaction.status, 'en')}`}
                      color={statusConfig.color}
                      size="small"
                    />
                  </div>
                  
                  {paymentTransaction.payment_type && (
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Metode Pembayaran / Payment Method
                      </Typography>
                      <Typography variant="body1">
                        {paymentTransaction.payment_type}
                      </Typography>
                    </div>
                  )}
                  
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      Tanggal / Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(paymentTransaction.created_at).toLocaleString('id-ID')}
                    </Typography>
                  </div>
                  
                  {paymentTransaction.paid_at && (
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Dibayar Pada / Paid At
                      </Typography>
                      <Typography variant="body1">
                        {new Date(paymentTransaction.paid_at).toLocaleString('id-ID')}
                      </Typography>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Alert severity="info" className="mb-4">
              <Typography variant="body2">
                {statusMessage.details}
              </Typography>
              <Typography variant="body2" color="textSecondary" className="mt-1">
                {statusMessage.detailsEn}
              </Typography>
            </Alert>
          </Box>
        )}

        <Divider />

        {/* Action Buttons */}
        <Box className="p-6 text-center">
          <div className="space-x-4">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/invoices')}
              startIcon={<ReceiptIcon />}
              size="large"
            >
              Lihat Invoice / View Invoices
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              startIcon={<HomeIcon />}
              size="large"
            >
              Beranda / Home
            </Button>
            
            {paymentTransaction && (paymentTransaction.status === 'PENDING' || paymentTransaction.status === 'FAILED') && (
              <Button
                variant="outlined"
                onClick={checkPaymentStatus}
                startIcon={<RefreshIcon />}
                size="large"
              >
                {t('payment.refreshStatus')}
              </Button>
            )}
          </div>
        </Box>
      </Paper>
    </Container>
  );
};

// Individual status pages
export const PaymentSuccessPage: React.FC = () => <PaymentStatusPage status="success" />;
export const PaymentPendingPage: React.FC = () => <PaymentStatusPage status="pending" />;
export const PaymentErrorPage: React.FC = () => <PaymentStatusPage status="error" />;

export default PaymentStatusPage;